import React, { useMemo, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { Task } from "../state/Task";
import { emptyPlan } from "../state/reducer";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths, isAfter, startOfDay } from "date-fns";
import { ru } from "date-fns/locale";
import { useData } from "../storage/accessors";
import "./taskSchedule.css";

const ScheduleTab: React.FC<{ task: Task }> = ({ task }) => {
    const { plans } = useData();
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // Получаем все планы для этой задачи
    const taskPlans = useMemo(() => {
        return Object.values(plans.objects).filter(plan => plan.task === task.uuid);
    }, [plans.objects, task.uuid]);

    // Получаем все планы на даты текущего месяца (для подсчёта plansCount)
    const allPlansByDate = useMemo(() => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const daysInMonth = eachDayOfInterval({ start, end });
        const dateStrs = new Set(daysInMonth.map(d => format(d, 'yyyy-MM-dd')));
        
        const result: Record<string, number> = {};
        Object.values(plans.objects).forEach(plan => {
            if (dateStrs.has(plan.date)) {
                result[plan.date] = (result[plan.date] || 0) + 1;
            }
        });
        return result;
    }, [plans.objects, currentMonth]);

    // Получаем даты с планами для быстрого поиска
    const datesWithPlans = useMemo(() => {
        return new Set(taskPlans.map(plan => plan.date));
    }, [taskPlans]);

    // Получаем дни с выполненными планами
    const datesWithCompletedPlans = useMemo(() => {
        return new Set(taskPlans.filter(plan => plan.isDone).map(plan => plan.date));
    }, [taskPlans]);

    // Генерируем дни для текущего месяца
    const calendarDays = useMemo(() => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        return eachDayOfInterval({ start, end }).map(date => {
            const dateStr = format(date, 'yyyy-MM-dd');
            return {
                date,
                dateStr,
                hasPlan: datesWithPlans.has(dateStr),
                isCompleted: datesWithCompletedPlans.has(dateStr),
                plansCount: allPlansByDate[dateStr] || 0
            };
        });
    }, [currentMonth, datesWithPlans, datesWithCompletedPlans, allPlansByDate]);

    // Группируем дни по месяцам для отображения
    const monthsData = useMemo(() => {
        const monthKey = format(currentMonth, 'yyyy-MM');
        return [{
            month: currentMonth,
            days: calendarDays
        }];
    }, [calendarDays, currentMonth]);

    const handleDayClick = (day: { date: Date, dateStr: string, hasPlan: boolean, isCompleted: boolean, plansCount: number }) => {
        const isPast = isAfter(startOfDay(new Date()), startOfDay(day.date));

        if (day.hasPlan) {
            // Удаляем план (выполненный или нет) - можно удалять даже прошлые даты
            const planToDelete = taskPlans.find(plan => plan.date === day.dateStr);
            if (planToDelete) {
                plans.delete(planToDelete.uuid);
            }
        } else if (!isPast) {
            // Создаем план только на будущие даты или сегодня
            const newPlan = emptyPlan(task, day.date, 0);
            plans.create(newPlan);
        }
    };

    const isCurrentMonth = (date: Date) => isSameMonth(date, currentMonth);

    const isPastDate = (date: Date) => {
        const today = startOfDay(new Date());
        const checkDate = startOfDay(date);
        return isAfter(today, checkDate);
    };

    const isFutureOrTodayDate = (date: Date) => {
        return !isPastDate(date);
    };

    // Определение цвета точек в зависимости от количества задач
    const getDotColors = (count: number) => {
        if (count === 0) {
            return ['secondary', 'secondary', 'secondary'];
        } else if (count <= 2) {
            return ['success', 'secondary', 'secondary'];
        } else if (count <= 5) {
            return ['warning', 'warning', 'secondary'];
        } else {
            return ['danger', 'danger', 'danger'];
        }
    };

    return (
        <div className="mt-2">
            {/* Заголовок с навигацией */}
            <Row className="mb-3">
                <Col className="d-flex justify-content-between align-items-center">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    >
                        ← Предыдущий месяц
                    </Button>
                    <h6 className="mb-0 text-center">
                        {format(currentMonth, 'LLLL yyyy', { locale: ru })}
                    </h6>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    >
                        Следующий месяц →
                    </Button>
                </Col>
            </Row>

            {/* Календарь на 1 месяц */}
            <Row>
                {monthsData.map(({ month, days }) => (
                    <Col key={format(month, 'yyyy-MM')} xs={12}>
                        <div className="border rounded p-2">
                            <div className="calendar-grid">
                                {/* Заголовки дней недели */}
                                <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(dayName => (
                                        <div key={dayName} className="text-center fw-bold small text-secondary">
                                            {dayName}
                                        </div>
                                    ))}
                                </div>

                                {/* Дни месяца */}
                                <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                                    {/* Пустые ячейки для выравнивания */}
                                    {(() => {
                                        // Получаем день недели для первого дня месяца (0=воскресенье, 1=понедельник, ..., 6=суббота)
                                        const firstDayOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
                                        const dayOfWeek = firstDayOfMonth.getDay();
                                        // Конвертируем в формат где 0=понедельник, 1=вторник, ..., 6=воскресенье
                                        const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                                        return Array.from({ length: offset }, (_, i) => (
                                            <div key={`empty-${i}`}></div>
                                        ));
                                    })()}

                                    {days.map(day => {
                                        const isPast = isPastDate(day.date);
                                        const canClick = !isPast;
                                        const dotColors = getDotColors(day.plansCount);
                                        const hasCurrentTaskPlan = day.hasPlan;

                                        return (
                                            <div
                                                key={day.dateStr}
                                                className={`
                                                    calendar-day-card
                                                    p-1 text-center
                                                    ${day.isCompleted ? 'bg-success text-white' :
                                                        hasCurrentTaskPlan ? 'calendar-day-has-plan' :
                                                            canClick ? 'bg-body-tertiary' : ''}
                                                    ${isCurrentMonth(day.date) ? 'current-month' : 'other-month'}
                                                    ${!isCurrentMonth(day.date) ? 'opacity-50' : ''}
                                                    ${canClick && !hasCurrentTaskPlan ? 'hover:bg-secondary hover:text-white' : ''}
                                                    ${canClick ? 'cursor-pointer' : 'cursor-not-allowed'}
                                                    ${isPast && !day.isCompleted ? 'calendar-day-past' : ''}
                                                `}
                                                onClick={() => handleDayClick(day)}
                                                title={
                                                    day.isCompleted ? 'Выполненная задача. Нажмите чтобы удалить план' :
                                                        hasCurrentTaskPlan ? (isPast ? 'Прошлый план. Нажмите чтобы удалить' : 'Нажмите чтобы удалить план') :
                                                            isPast ? 'Нельзя планировать на прошлые даты' :
                                                                'Нажмите чтобы создать план'
                                                }
                                            >
                                                <div className={`calendar-day-number${isPast && !day.isCompleted ? ' is-past' : ''}`}>{format(day.date, 'd')}</div>
                                                <div className="calendar-dots d-flex justify-content-center gap-1 mt-1">
                                                    {dotColors.map((color, idx) => (
                                                        <span
                                                            key={idx}
                                                            className={`calendar-dot bg-${color}`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>

            {/* Легенда */}
            <Row className="mt-3">
                <Col>
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                        <div className="d-flex align-items-center gap-1">
                            <div className="bg-success rounded" style={{ width: '16px', height: '16px' }}></div>
                            <small>Выполненные задачи</small>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <div className="bg-primary rounded" style={{ width: '16px', height: '16px' }}></div>
                            <small>Дни с планами</small>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <div className="bg-body-tertiary border rounded" style={{ width: '16px', height: '16px' }}></div>
                            <small>Доступные дни</small>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <div className="border rounded d-flex align-items-center justify-content-center" style={{ width: '16px', height: '16px' }}>
                                <small className="text-secondary" style={{ textDecoration: 'line-through', lineHeight: 1 }}>4</small>
                            </div>
                            <small>Прошлые дни</small>
                        </div>
                        <small className="text-muted">
                            Нажмите на день для создания/удаления плана
                        </small>
                    </div>
                </Col>
            </Row>

        </div>
    );
};

export default ScheduleTab;